import type { SupabaseClient } from '../../lib/supabase';
import type {
  OwnSupplierOrganization,
  SupplierOrganization,
  SupplierOrganizationInput,
  SupplierProfileInput,
} from './types';

/**
 * Database access for supplier organizations.
 *
 * Row Level Security decides everything: TAP Admins see and manage all
 * organizations; a supplier's read returns at most their own active
 * organization. Nothing here filters for authorization, and no query loads
 * organization data at application startup.
 */

function fail(message: string): never {
  throw new Error(message);
}

/** Every organization, admin only by policy. Alphabetical for a stable list. */
export async function fetchSupplierOrganizations(
  client: SupabaseClient,
): Promise<SupplierOrganization[]> {
  const { data, error } = await client
    .from('supplier_organizations')
    .select('*')
    .order('name', { ascending: true });

  if (error) {
    fail(error.message);
  }
  return data ?? [];
}

/**
 * The signed-in supplier's own organization: the only row their policy can
 * return, and only while both their profile and the organization are active.
 * Selects the minimum safe columns; a null result renders the unavailable
 * state.
 */
/**
 * The organization the portal is showing.
 *
 * `organizationId` is required, and that is the whole fix. This used to be a
 * bare limit(1).maybeSingle() that trusted RLS to return exactly one row —
 * true for a real supplier, and false the moment a super admin could see
 * every organization. It would then have returned an arbitrary one and looked
 * like it worked.
 */
export async function fetchOwnSupplierOrganization(
  client: SupabaseClient,
  organizationId: string,
): Promise<OwnSupplierOrganization | null> {
  if (organizationId === '') {
    return null;
  }
  const { data, error } = await client
    .from('supplier_organizations')
    .select('id, name')
    .eq('id', organizationId)
    .maybeSingle();

  if (error) {
    fail(error.message);
  }
  return data;
}

export async function createSupplierOrganization(
  client: SupabaseClient,
  input: SupplierOrganizationInput & { createdBy: string },
): Promise<SupplierOrganization> {
  const contactEmail = input.contactEmail.trim();
  const { data, error } = await client
    .from('supplier_organizations')
    .insert({
      name: input.name.trim(),
      code: input.code,
      contact_email: contactEmail === '' ? null : contactEmail,
      // Self-attribution is enforced by RLS; any other value is refused.
      created_by: input.createdBy,
    })
    .select()
    .maybeSingle();

  if (error) {
    fail(error.message);
  }
  if (!data) {
    fail('organization_creation_failed');
  }
  return data;
}

export async function updateSupplierOrganization(
  client: SupabaseClient,
  id: string,
  input: SupplierOrganizationInput,
): Promise<SupplierOrganization> {
  const contactEmail = input.contactEmail.trim();
  const { data, error } = await client
    .from('supplier_organizations')
    .update({
      name: input.name.trim(),
      code: input.code,
      contact_email: contactEmail === '' ? null : contactEmail,
    })
    .eq('id', id)
    .select()
    .maybeSingle();

  if (error) {
    fail(error.message);
  }
  if (!data) {
    // RLS filtered the row: the caller is not permitted to update it.
    fail('permission denied for organization update');
  }
  return data;
}

/**
 * The factory's operational settings: who the PO is addressed to, where they
 * are, and their realistic sample turnaround.
 *
 * A database function rather than a table update, because these three decide
 * what a supplier is committed to and what a PO says — and is_tap_admin() at
 * the database is a stronger guarantee than an RLS policy somebody could widen
 * for an unrelated reason later.
 */
export async function updateSupplierProfile(
  client: SupabaseClient,
  id: string,
  input: SupplierProfileInput,
): Promise<void> {
  const { error } = await client.rpc('update_supplier_profile', {
    p_org: id,
    p_contact_name: input.contactName.trim(),
    p_address: input.address.trim(),
    // The generated types cannot express a NULLABLE function argument — every
    // parameter comes through as its non-null type — but the function takes
    // null deliberately: it is how "this factory has no default, use the
    // workspace number" is said, and without it a default could be set and
    // never cleared. Narrowed here at the one boundary rather than by widening
    // the shared type.
    p_default_sample_due_days: input.defaultSampleDueDays as number,
    p_default_production_days: input.defaultProductionDays as number,
  });
  if (error) {
    fail(error.message);
  }
}

/** Activation and deactivation are plain admin updates of is_active. */
export async function setSupplierOrganizationActive(
  client: SupabaseClient,
  id: string,
  isActive: boolean,
): Promise<SupplierOrganization> {
  const { data, error } = await client
    .from('supplier_organizations')
    .update({ is_active: isActive })
    .eq('id', id)
    .select()
    .maybeSingle();

  if (error) {
    fail(error.message);
  }
  if (!data) {
    fail('permission denied for organization update');
  }
  return data;
}

const ORGANIZATION_ERROR_MATCHERS: Array<{ needle: string; message: string }> = [
  {
    needle: 'supplier_organizations_code_key',
    message: 'That code is already used by another organization.',
  },
  {
    needle: 'supplier_organizations_code_format',
    message: 'Codes are 2-32 characters: lowercase letters, numbers, and hyphens.',
  },
  {
    needle: 'supplier_organizations_name_present',
    message: 'Enter an organization name.',
  },
  {
    needle: 'organization_creator_must_be_active_tap_admin',
    message: 'Only an active TAP admin can create organizations.',
  },
  {
    needle: 'permission denied',
    message: 'Your account is not allowed to manage supplier organizations.',
  },
];

/** Turns a database error message into something a person can act on. */
export function organizationErrorMessage(message: string): string {
  const match = ORGANIZATION_ERROR_MATCHERS.find((entry) => message.includes(entry.needle));
  return match?.message ?? 'Could not save the organization. Try again.';
}
